// Removed outdated import
import { signOut } from '@aws-amplify/auth'
import axios from 'axios'
import moment from 'moment'
import { getBackendBaseUrl } from './config/config'
import { attachSentryResponseInterceptor } from './sentry'
import { deleteAmplifyCookies } from './util/UtilFunctions'
import { clearAuthSessionState, getCountdownTime, setCountdownTime, getLastActiveTime, SESSION_TIMEOUT_MS } from './util/tokenStorage'
import { clearCachedTokens, getAccessToken } from './util/authUtil'

// x-api-key sent only for internal (no-JWT) API calls; not used for user-authenticated requests
const INTERNAL_API_KEY = process.env.REACT_APP_INTERNAL_API_KEY || process.env.REACT_APP_TBD_API_KEY

let _onAuthFailure = null
export const setAuthFailureHandler = handler => { _onAuthFailure = handler }

/**
 * Thrown by `__buildSecureHeaders` when an auth-required request is attempted
 * without a valid access token. Carries a synthetic axios-shaped `response`
 * with status 401 so generic error handlers (e.g. `parseHelper.js`) and any
 * `err.response?.status === 401` branches behave consistently. Use
 * `instanceof AuthTokenMissingError` for type checks rather than matching the
 * `.message` string (which is mangled by minifiers).
 */
export class AuthTokenMissingError extends Error {
  constructor(message = 'AUTH_TOKEN_MISSING') {
    super(message)
    this.name = 'AuthTokenMissingError'
    this.response = { status: 401 }
  }
}

// Dedicated axios client for ApiService. Keeping the 401 auth-failure
// interceptor off the global `axios` instance avoids cross-module coupling
// with `sentry.js` (which also installs a response interceptor on global
// axios) and removes any dependency on import order.
const api = axios.create()

api.interceptors.response.use(
  response => response,
  error => {
    const status = error?.response?.status
    // Some endpoints return 401 for a non-session reason (e.g. the result-file
    // DOB gate returns 401 for a wrong date of birth). Those requests opt out
    // of the global auth-failure handler via `skipAuthFailureHandler` so a
    // wrong DOB shows an inline retry instead of logging the user out.
    if (status === 401 && !error?.config?.skipAuthFailureHandler) {
      if (_onAuthFailure) _onAuthFailure()
    }
    return Promise.reject(error)
  },
)

attachSentryResponseInterceptor(api)

const axiosCodesToRetry = ['ERR_NETWORK', 'ECONNABORTED', 'ECONNREFUSED', 'ECONNRESET', 'ETIMEDOUT', 'ERR_INTERNET_DISCONNECTED']

const DEFAULT_RETRY_COUNT = 3 // Number of retry attempts
const DEFAULT_RETRY_DELAY_MS = 5000 // Delay between retries in milliseconds
const MAX_RETRY_DELAY_MS = 30000 // Maximum backoff delay cap

export class InternalApiService {
  async timeLimitCheck() {
    const countdownTime = getCountdownTime()
    const lastActive = getLastActiveTime() || countdownTime
    const lastAuthMoment = lastActive ? moment(lastActive) : null

    // if more than SESSION_TIMEOUT_MS has elapsed, signout and clear session storage
    if (lastAuthMoment && moment().diff(lastAuthMoment) > SESSION_TIMEOUT_MS) {
      try {
        await signOut({ global: true })
      } catch (err) {
        console.error(err)
      }
      deleteAmplifyCookies()
      clearAuthSessionState()
      clearCachedTokens()
      if (window.location.pathname !== '/login') {
        window.location.replace('/login')
      } else {
        window.location.replace('/')
      }
      return null
    } else {
      try {
        const accessToken = await getAccessToken()
        if (!accessToken) return null
        const nowIso = moment().toISOString()
        setCountdownTime(nowIso)
        return true
      } catch (err) {
        console.error(err)
        return null
      }
    }
  }

  async deleteSharedUser(recipientUUID) {
    await this.timeLimitCheck()
    return this.__sendRequest(`/secure/usersharing/share/${recipientUUID}`, 'DELETE')
  }

  async getDuplicateEmailCheck(email) {
    return await this.__sendRequestNoToken(`/secure/user/email?email=${email}`, 'GET')
  }

  async getKitExists() {
    await this.timeLimitCheck()
    return this.__sendRequest('/secure/kit/exists', 'GET')
  }

  async getMessageCount() {
    await this.timeLimitCheck()
    return this.__sendRequest('/secure/message/new/count', 'GET')
  }

  async getMessage(messageUUID) {
    await this.timeLimitCheck()
    return this.__sendRequest(`/secure/message/${messageUUID}`, 'GET')
  }

  async getMessages() {
    await this.timeLimitCheck()
    return this.__sendRequest('/secure/message', 'GET')
  }

  async getPatientAfterSessRef() {
    await this.timeLimitCheck()
    return this.__sendRequest('/secure/patient', 'GET')
  }

  async getSentMessages() {
    await this.timeLimitCheck()
    return this.__sendRequest('/secure/message/sent', 'GET')
  }

  async getSharedUsers(kitId) {
    await this.timeLimitCheck()
    return this.__sendRequest(`/secure/usersharing/${kitId}/share`, 'GET')
  }

  async getUserHistory() {
    await this.timeLimitCheck()
    return this.__sendRequest('/secure/userhistory', 'GET')
  }

  async getUserHistoryDownload(kitId, labResultName, responseType) {
    await this.timeLimitCheck()
    return this.__sendRequest(`/secure/userhistory/v2/download/${kitId}/${labResultName}`, 'GET', responseType)
  }

  async validatePwdFor(kitId, fileName, pwd) {
    // timeLimitCheck() + __buildSecureHeaders validate/refresh the session
    // first; a missing or unrefreshable token throws AuthTokenMissingError and
    // the global handler logs the user out before the request is sent. Because
    // the session is confirmed immediately beforehand, a 401 *response* from
    // verifyFilePwd is treated as "wrong DOB" (inline retry) rather than an
    // auth failure — hence skipAuthFailureHandler. (A genuinely revoked-but-
    // unexpired token is the only residual case, and the next secure call
    // re-triggers logout.)
    await this.timeLimitCheck()
    const reqBody = { kitId, fileName, pwd }
    return this.__postOrPutRequest(`/secure/userhistory/verifyFilePwd`, 'POST', reqBody, { skipAuthFailureHandler: true })
  }

  async getUserHistoryView(kitId, labResultName, responseType) {
    await this.timeLimitCheck()
    return this.__sendRequest(`/secure/userhistory/v2/viewTestResult/${kitId}/${labResultName}`, 'GET', responseType)
  }

  async sendRequestToDownloadResultFile(kitId) {
    await this.timeLimitCheck()
    return this.__sendRequest(`/secure/userhistory/v2/reqResultDownload/${kitId}`, 'GET')
  }

  async getUserSubscriptions() {
    await this.timeLimitCheck()
    return this.__sendRequest('/secure/patient/subscription', 'GET')
  }

  async getUserSharing(kitId) {
    await this.timeLimitCheck()
    return this.__sendRequest(`/secure/usersharing/${kitId}/summary`, 'GET')
  }

  async getOrderFromInbox() {
    await this.timeLimitCheck()
    return this.__sendRequest('/secure/patient/orders', 'GET')
  }

  async postMessage(data) {
    await this.timeLimitCheck()
    return this.__postOrPutRequest('/secure/message', 'POST', data)
  }

  async postRegisterKit(data) {
    await this.timeLimitCheck()
    return this.__postOrPutRequest('/secure/kit', 'POST', data)
  }

  async postHelp(data) {
    await this.timeLimitCheck()
    return this.__postOrPutRequest('/secure/question', 'POST', data)
  }

  async postReplyMessage(data) {
    await this.timeLimitCheck()
    return this.__postOrPutRequest('/secure/message/reply', 'POST', data)
  }

  async postUserPic(data) {
    await this.timeLimitCheck()
    return this.__postOrPutRequest(`/secure/user/pic`, 'POST', data)
  }

  async postUserSharingRecipients(kitId, data) {
    await this.timeLimitCheck()
    return this.__postOrPutRequest(`/secure/usersharing/${kitId}/share`, 'POST', data)
  }

  async postWelcomeEmail(data) {
    return this.__postRequestWithInternalKey(`/secure/message/welcome`, 'POST', data)
  }

  async postUserHealthData(data) {
    return this.__postRequestWithInternalKey(`/secure/patient`, 'POST', data)
  }

  async updateUserHealthData(data) {
    return this.__postOrPutRequest(`/secure/patient/updatePatientDetails`, 'POST', data)
  }

  async acknowledgeMessage(messageUUID) {
    await this.timeLimitCheck()
    return this.__postOrPutRequest(`/secure/message/acknowledge/${messageUUID}`, 'POST', messageUUID)
  }

  async createSubSkipShippingRequest(data) {
    return this.__postOrPutRequest(`/secure/patient/subscription/skip`, 'POST', data)
  }
  async createSubModifyRequest(data) {
    return this.__postOrPutRequest(`/secure/patient/subscription/modify`, 'POST', data)
  }
  async createSubCancelRequest(data) {
    return this.__postOrPutRequest(`/secure/patient/subscription/cancel`, 'POST', data)
  }

  async hasHealthProfile() {
    await this.timeLimitCheck()
    return this.__sendRequest(`/secure/patient/hasHealthProfile`, 'GET')
  }

  async uploadFile(data, fileUseCaseType) {
    const formData = new FormData()
    formData.append('file', data)
    return await this.__uploadMultiPartFileRequest(`/secure/file/upload/${fileUseCaseType}`, 'POST', formData, 'multipart/form-data')
  }

  async downloadFile(fileUseCaseType, fileName) {
    return await this.__sendRequest(`/secure/file/download/${fileUseCaseType}/${fileName}`, 'GET', 'blob')
  }

  async deleteFile(fileUseCaseType, fileName) {
    return await this.__sendRequest(`/secure/file/delete/${fileUseCaseType}/${fileName}`, 'DELETE')
  }

  async hasHealthProfileSync() {
    let status = null
    await this.timeLimitCheck()
    await this.__sendRequest(`/secure/patient/hasHealthProfile`, 'GET')
      .then(resp => (status = resp?.data))
      .catch(err => {
        status = 'unknown'
      })
    return status
  }

  async __retryAxios(config, retries = DEFAULT_RETRY_COUNT, delay = DEFAULT_RETRY_DELAY_MS) {
    // Only retry GET (fetch) calls; other methods (POST/PUT/DELETE) run once
    if (config.method && config.method.toLowerCase() !== 'get') {
      return api(config)
    }

    const isRetryable = error => {
      // Do not retry if request was cancelled
      if (axios.isCancel(error)) return false
      // Retry on network or timeout codes
      if (error.code && axiosCodesToRetry.includes(error.code)) return true
      // Retry only on gateway/server errors
      const status = error.response?.status
      return status === 502 || status === 503 || status === 504
    }

    // First attempt
    try {
      return await api(config)
    } catch (initialError) {
      // Bail immediately on non-retryable errors or if window closed
      if (!isRetryable(initialError) || window.closed) {
        throw initialError
      }

      // Retry loop
      for (let attempt = 1; attempt <= retries; attempt++) {
        // Abort if window was closed during wait
        if (window.closed) {
          throw new Error('Window closed, aborting retries')
        }
        // Calculate exponential backoff with cap
        const backoff = Math.min(delay * Math.pow(2, attempt - 1), MAX_RETRY_DELAY_MS)
        if (backoff > 0) {
          await new Promise(res => setTimeout(res, backoff))
        }
        try {
          return await api(config)
        } catch (error) {
          // If no further retries or non-retryable, bubble up
          if (attempt === retries || !isRetryable(error)) {
            throw error
          }
        }
      }
    }
  }

  async __buildSecureHeaders(extraHeaders = {}, includeAuth = true) {
    const authToken = includeAuth ? await getAccessToken() : null
    if (includeAuth && !authToken) {
      if (_onAuthFailure) _onAuthFailure()
      throw new AuthTokenMissingError()
    }
    return {
      ...(authToken ? { authorization: `Bearer ${authToken}` } : {}),
      addresseeType: 'P',
      'content-type': 'application/json',
      ...extraHeaders,
    }
  }

  async __sendRequest(url, method, responseType = null) {
    const headers = await this.__buildSecureHeaders()
    if (responseType === 'blob') {
      return this.__retryAxios({
        method: method,
        url: getBackendBaseUrl() + url,
        responseType: responseType,
        headers,
      })
    } else {
      return this.__retryAxios({
        method: method,
        url: getBackendBaseUrl() + url,
        headers,
      })
    }
  }

  async __uploadMultiPartFileRequest(url, method, data, contentType) {
    const headers = await this.__buildSecureHeaders({ 'content-type': contentType })
    return this.__retryAxios({
      method: method,
      url: getBackendBaseUrl() + url,
      headers,
      data: data,
    })
  }

  async __sendRequestNoToken(url, method) {
    return this.__retryAxios({
      method: method,
      url: getBackendBaseUrl() + url,
      headers: {
        'content-type': 'application/json',
      },
    })
  }

  /**
   * POST/PUT with x-api-key (INTERNAL_API_KEY) only — no JWT.
   * Use for internal/server-style endpoints (e.g. welcome email, patient create).
   */
  __postRequestWithInternalKey(url, method, data) {
    const headers = {
      'content-type': 'application/json',
      ...(INTERNAL_API_KEY ? { 'x-api-key': INTERNAL_API_KEY } : {}),
    }
    return this.__retryAxios({
      method: method,
      url: getBackendBaseUrl() + url,
      headers,
      data: data,
    })
  }

  async __postOrPutRequest(url, method, data, options = {}) {
    const headers = await this.__buildSecureHeaders()
    return this.__retryAxios({
      method: method,
      url: getBackendBaseUrl() + url,
      headers,
      data: data,
      skipAuthFailureHandler: options.skipAuthFailureHandler,
    })
  }

  __postRequestNoToken(url, method, data) {
    return this.__retryAxios({
      method: method,
      url: getBackendBaseUrl() + url,
      headers: {
        'content-type': 'application/json',
      },
      data: data,
    })
  }
}

const apiServiceInstance = new InternalApiService()
export default apiServiceInstance
